import React, { createContext, PropsWithChildren, useContext } from 'react';

type AscendingSortDirection = 'asc';
type DescendingSortDirection = 'desc';

export type SortDirection = AscendingSortDirection | DescendingSortDirection;

export type OnSortChange = (columnKey: string, newSortDirection: SortDirection) => void;

type HandleSortChange = (columnKey: string, columnInitalSortDirection?: SortDirection) => void;

interface TableSortContextValues {
  activeSortColumnKey: string | null;
  activeSortDirection?: SortDirection;
  onSortChange: HandleSortChange;
  setActiveSortColumn?: (columnKey: string) => void;
  setActiveSortDirection?: (direction: SortDirection) => void;
}

export const TableSortContext = createContext<TableSortContextValues>({
  activeSortColumnKey: null,
  activeSortDirection: 'asc',
  onSortChange: () => undefined,
  setActiveSortColumn: () => undefined,
  setActiveSortDirection: () => undefined,
});

export interface TableSortContextProviderProps {
  activeSortColumnKey?: string | null;
  activeSortDirection?: SortDirection;
  onSortChange?: OnSortChange;
  setActiveSortColumn?: (columnKey: string) => void;
  setActiveSortDirection?: (direction: SortDirection) => void;
}

export const TableSortContextProvider: React.FunctionComponent<
  PropsWithChildren<TableSortContextProviderProps>
> = ({
  activeSortColumnKey = null,
  activeSortDirection = 'asc',
  onSortChange = () => undefined,
  setActiveSortColumn,
  setActiveSortDirection,
  children,
}) => {
  const handleSortChange: TableSortContextValues['onSortChange'] = (
    columnKey,
    initialSortDirection = 'asc',
  ) => {
    const isActiveColumn = columnKey === activeSortColumnKey;
    let newSortDirection: SortDirection;
    if (!isActiveColumn) newSortDirection = initialSortDirection;
    else newSortDirection = activeSortDirection === 'asc' ? 'desc' : 'asc';
    onSortChange(columnKey, newSortDirection);
  };

  return (
    <TableSortContext.Provider
      value={{
        activeSortColumnKey,
        activeSortDirection,
        onSortChange: handleSortChange,
        setActiveSortColumn,
        setActiveSortDirection,
      }}
    >
      {children}
    </TableSortContext.Provider>
  );
};

export const useTableSortContext = () => useContext(TableSortContext);
